import 'lazysizes';
import { createApp } from 'vue';
import { createStore } from 'vuex';

import { hoistJsonScripts } from '@Foundation/utilities';

// Plugins for zone-level customizations
import loadZoneCustomizations from '@refapp/zone-plugin-loader';
import init from '@refapp/sisyphus';

// Sitecore-Vue bridge
import SitecoreVueAdapter from '@Foundation/sitecore/SitecoreVueAdapter';

// Helpers
import { requireAll } from '@Foundation/utilities/helpers';

// Foundation
import Foundation from '@Foundation';
import LoadScriptModule from '@Foundation/loadscript';
import eventBus, { exposeGlobals } from '@refapp/eventbus-js';
import { tag } from '@Foundation/analyticsHandler/directives/tag';

// Feature
import CookieBanner from '@Feature/PageContentExt';
import HeaderLogo from '@Feature/HeaderLogo';
import LiveChat from '@Feature/LiveChat';
import ModalModule from '@Feature/Modal';
import NavigationModule from '@Feature/Navigation';
import NewsletterModule from '@Feature/NewsLetter';
import OapAccountNavigation from '@Feature/OapAccountNavigation';
import OapBadge from '@Feature/OapBadge';
import OapBeautyGeniusWrapper from '@Feature/OapBeautyGeniusWrapper';
import OapCard from '@Feature/OapCard';
import OapCarouselControls from '@Feature/CarouselControls';
import OapCountdown from '@Feature/OapCountdown';
import OapEventfulTextInput from '@Feature/OapEventfulTextInput';
import OapLoader from '@Feature/OapLoader';
import OapMediaCover from '@Feature/OapMediaCover';
import OapPopin from '@Feature/OapPopin';
import OapPromoBar from '@Feature/OapPromoBar';
import OapRating from '@Feature/OapRating';
import OapScroller from '@Feature/OapScroller';
import OapSlider from '@Feature/OapSlider';
import OapStrikethroughScrolling from '@Feature/OapStrikethroughScrolling';
import OneTrustCookieHelper from '@Feature/OneTrust';
import Overlay from '@Feature/Overlay';
import Search from '@Feature/Search';
import SearchSuggestions from '@Feature/SearchSuggestions';
import Slider from '@Feature/Slider';
import VideoPlaceholder from '@Feature/VideoPlaceholder';
import OapPageNavigation from '@Feature/OapPageNavigation';
import AccessibilityStore from '@Foundation/accessibilityStore';
import OapPromoCard from '@Feature/OapPromoCard';
import FloatingSidebar from '@Feature/FloatingSidebar';
import OapMikMak from '@Feature/OapMikMak';
import OapRegisterNowPopup from '@Feature/OapRegisterNowPopup';
import WishList from '@Feature/WishList';

exposeGlobals();

const app = createApp({});
app.config.compilerOptions.whitespace = 'preserve';
app.config.performance = true;

// Register directives
Object.entries({
  ...Foundation.directives,
  ...LoadScriptModule.directives,
  ...NavigationModule.directives,
  ...Search.directives,
  tag,
}).forEach(([name, directive]) => app.directive(name, directive));

// Register components
Object.entries({
  ...CookieBanner.components,
  ...Foundation.components,
  ...HeaderLogo.components,
  ...LiveChat.components,
  ...LoadScriptModule.components,
  ...ModalModule.components,
  ...NavigationModule.components,
  ...NewsletterModule.components,
  ...OapAccountNavigation.components,
  ...OapBadge.components,
  ...OapBeautyGeniusWrapper.components,
  ...OapCard.components,
  ...OapCarouselControls.components,
  ...OapCountdown.components,
  ...OapEventfulTextInput.components,
  ...OapLoader.components,
  ...OapMediaCover.components,
  ...OapPopin.components,
  ...OapPromoBar.components,
  ...OapRating.components,
  ...OapScroller.components,
  ...OapSlider.components,
  ...OapStrikethroughScrolling.components,
  ...OneTrustCookieHelper.components,
  ...Overlay.components,
  ...SearchSuggestions.components,
  ...Slider.components,
  ...VideoPlaceholder.components,
  ...OapPageNavigation.components,
  ...OapPromoCard.components,
  ...FloatingSidebar.components,
  ...OapMikMak.components,
  ...OapRegisterNowPopup.components,
  ...WishList.components,
}).forEach(([name, component]) => app.component(name, component));

Object.entries({
  ...Foundation.filters,
}).forEach(([name, filter]) => app.filter(name, filter));
const store = createStore({
  modules: {
    ...AccessibilityStore.modules,
  },
});

app.use(store);
/**
 * Initialize any zone-level customizations.
 * Every zone/country can create their own Vue components and make them available
 * to their code through the use of a Vue.js Plugin
 *
 * @see https://vuejs.org/guide/reusability/plugins.html#writing-a-plugin
 * */
loadZoneCustomizations(app, {});
// Initialize third party loader
init();

requireAll(require.context('../../../Icons/', true, /\.svg$/));

document.addEventListener('lazybeforeunveil', (event) =>
  eventBus.emit('image.lazybeforeunveil', event)
);
window.RefAppMctb && app.use(window.RefAppMctb);
window.RefAppAmp && app.use(window.RefAppAmp);
window.RefAppStoreLocator && app.use(window.RefAppStoreLocator);
window.RefAppFaq && app.use(window.RefAppFaq);
window.RefAppRatingsReviews && app.use(window.RefAppRatingsReviews);
window.RefAppBackToTop && app.use(window.RefAppBackToTop);
window.RefAppUserRedirect && app.use(window.RefAppUserRedirect);

hoistJsonScripts();

SitecoreVueAdapter.initializeVueApp(app.mount.bind(app, '#main-container'));
